/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { DateFormatter, DateFormatterOptions } from './formatter/date.formatter';
import { IndentFormatter, IndentFormatterOptions } from './formatter/indent.formatter';
import { JoinFormatter, JoinFormatterOptions } from './formatter/join.formatter';
import { NumberFormatter, NumberFormatterOptions } from './formatter/number.formatter';
import { PercentFormatter } from './formatter/percent.formatter';
import { CoreErrorKey } from './key/core-error.key';
import { CoreFormatterKey } from './key/core-formatter.key';
import { CoreModelKey } from './key/core-model.key';
import { CoreServiceKey } from './key/core-service.key';
import { CoreValidationKey } from './key/core-validation.key';
import { ApplicationErrorMessageFactory } from './message-factory/application-error.message-factory';
import { HttpErrorMessageFactory } from './message-factory/http-error.message-factory';
import { ComparatorValidationErrorMessageFactory } from './message-factory/comparator-validation-error.message-factory';
import { FieldValidationErrorMessageFactory } from './message-factory/field-validation-error.message-factory';
import { ObjectValidationErrorMessageFactory } from './message-factory/object-validation-error.message-factory';
import { ValidationErrorMessageFactory } from './message-factory/validation-error.message-factory';
import { ApplicationError } from './model/application-error.model';
import { ValidationError } from './model/validation-error.model';
import { FormatterProvider } from './provider/formatter.provider';
import { LocaleProvider } from './provider/locale.provider';
import { MessageFactoryProvider } from './provider/message-factory.provider';
import { ModelKeyProvider } from './provider/model-key.provider';
import { ServiceProvider } from './provider/service.provider';
import { ValidationProvider } from './provider/validation.provider';
import { LocaleService } from './service/locale.service';
import { MessageService } from './service/message.service';
import { AlertService } from './service/alert.service';
import { TextService } from './service/text.service';
import { ResourceService } from './service/resource.service';
import { validateDate } from './validation-predicate/date.validation-predicate';
import { validateEmail } from './validation-predicate/email.validation-predicate';
import { validateEqual } from './validation-predicate/equal.validation-predicate';
import { validateGreaterThanOrEqual } from './validation-predicate/greater-than-or-equal.validation-predicate';
import { validateGreaterThan } from './validation-predicate/greater-than.validation-predicate';
import { validateIterable } from './validation-predicate/iterable.validation-predicate';
import { validateLessThanOrEqual } from './validation-predicate/less-than-or-equal.validation-predicate';
import { validateLessThan } from './validation-predicate/less-than.validation-predicate';
import { validateNotEqual } from './validation-predicate/not-equal.validation-predicate';
import { validateNumber } from './validation-predicate/number.validation-predicate';
import { validateRequired } from './validation-predicate/required.validation-predicate';
import { validateType } from './validation-predicate/type.validation-predicate';

// DYNAMIC IMPORT IS NOT WORKING WITH COMMONJS MODULE RESOLUTION
import { coreDeLocale } from './locale/de.locale';
import { coreEnLocale } from './locale/en.locale';
import { validateHasValue } from './validation-predicate/has-value.validation-predicate';

export class CoreInitializer {

  static initialize() {
    CoreInitializer.registerModelKeys();
    CoreInitializer.registerServices();
    CoreInitializer.registerFormatterFactories();
    CoreInitializer.registerFormatters();
    CoreInitializer.registerValidationPredicates();
    CoreInitializer.registerMessageFactories();
    CoreInitializer.registerLocales();
  }

  static registerModelKeys() {
    ModelKeyProvider.registerKey(ApplicationError, CoreModelKey.APPLICATION_ERROR);
    ModelKeyProvider.registerKey(ValidationError, CoreModelKey.VALIDATION_ERROR);
  }

  static registerServices() {
    ServiceProvider.registerService(CoreServiceKey.LOCALE, new LocaleService());
    ServiceProvider.registerService(CoreServiceKey.TEXT, new TextService());
    ServiceProvider.registerService(CoreServiceKey.RESOURCE, new ResourceService());
    ServiceProvider.registerService(CoreServiceKey.MESSAGE, new MessageService());
    ServiceProvider.registerService(CoreServiceKey.ALERT, new AlertService(
      ServiceProvider.resolveService(CoreServiceKey.MESSAGE)
    ));
  }

  static registerFormatterFactories() {
    const numberFormatterFactory = (options?: NumberFormatterOptions) => new NumberFormatter(options);
    FormatterProvider.registerFormatterFactory('number', numberFormatterFactory);
    FormatterProvider.registerFormatterFactory(CoreFormatterKey.NUMBER, numberFormatterFactory);
    const percentFormatterFactory = (options?: NumberFormatterOptions) => new PercentFormatter(options);
    FormatterProvider.registerFormatterFactory('percent', percentFormatterFactory);
    FormatterProvider.registerFormatterFactory(CoreFormatterKey.PERCENT, percentFormatterFactory);
    const dateFormatterFactory = (options?: DateFormatterOptions) => new DateFormatter(options);
    FormatterProvider.registerFormatterFactory('date', dateFormatterFactory);
    FormatterProvider.registerFormatterFactory(CoreFormatterKey.DATE, dateFormatterFactory);
    const timeFormatterFactory = (options?: DateFormatterOptions) => new DateFormatter(options, CoreFormatterKey.TIME);
    FormatterProvider.registerFormatterFactory('time', timeFormatterFactory);
    FormatterProvider.registerFormatterFactory(CoreFormatterKey.TIME, timeFormatterFactory);
    const indentFormatterFactory = (options?: IndentFormatterOptions) => new IndentFormatter(options);
    FormatterProvider.registerFormatterFactory('indent', indentFormatterFactory);
    FormatterProvider.registerFormatterFactory(CoreFormatterKey.INDENT, indentFormatterFactory);
    const joinFormatterFactory = (options?: JoinFormatterOptions) => new JoinFormatter(options);
    FormatterProvider.registerFormatterFactory('join', joinFormatterFactory);
    FormatterProvider.registerFormatterFactory(CoreFormatterKey.JOIN, joinFormatterFactory);
  }

  static registerFormatters() {
    const numberFormatter = new NumberFormatter();
    FormatterProvider.registerFormatter('number', numberFormatter);
    FormatterProvider.registerFormatter(CoreFormatterKey.NUMBER, numberFormatter);
    const percentFormatter = new PercentFormatter();
    FormatterProvider.registerFormatter('percent', percentFormatter);
    FormatterProvider.registerFormatter(CoreFormatterKey.PERCENT, percentFormatter);
    const dateFormatter = new DateFormatter();
    FormatterProvider.registerFormatter('date', dateFormatter);
    FormatterProvider.registerFormatter(CoreFormatterKey.DATE, dateFormatter);
    const timeFormatter = new DateFormatter({ format: 'HH:mm:ss' }, CoreFormatterKey.TIME);
    FormatterProvider.registerFormatter('time', timeFormatter);
    FormatterProvider.registerFormatter(CoreFormatterKey.TIME, timeFormatter);
    const indentFormatter = new IndentFormatter();
    FormatterProvider.registerFormatter('indent', indentFormatter);
    FormatterProvider.registerFormatter(CoreFormatterKey.INDENT, indentFormatter);
    const joinFormatter = new JoinFormatter();
    FormatterProvider.registerFormatter('join', joinFormatter);
    FormatterProvider.registerFormatter(CoreFormatterKey.JOIN, joinFormatter);
  }

  static registerValidationPredicates() {
    ValidationProvider.registerPredicate(CoreValidationKey.HAS_VALUE, validateHasValue);
    ValidationProvider.registerPredicate(CoreValidationKey.REQUIRED, validateRequired);
    ValidationProvider.registerPredicate(CoreValidationKey.NUMBER, validateNumber);
    ValidationProvider.registerPredicate(CoreValidationKey.TYPE, validateType);
    ValidationProvider.registerPredicate(CoreValidationKey.EMAIL, validateEmail);
    ValidationProvider.registerPredicate(CoreValidationKey.DATE, validateDate);
    ValidationProvider.registerPredicate(CoreValidationKey.EQUAL, validateEqual);
    ValidationProvider.registerPredicate(CoreValidationKey.NOT_EQUAL, validateNotEqual);
    ValidationProvider.registerPredicate(CoreValidationKey.GREATER_THAN, validateGreaterThan);
    ValidationProvider.registerPredicate(CoreValidationKey.GREATER_THAN_OR_EQUAL, validateGreaterThanOrEqual);
    ValidationProvider.registerPredicate(CoreValidationKey.LESS_THAN, validateLessThan);
    ValidationProvider.registerPredicate(CoreValidationKey.LESS_THAN_OR_EQUAL, validateLessThanOrEqual);
    ValidationProvider.registerPredicate(CoreValidationKey.ITERABLE, validateIterable);
  }

  static registerMessageFactories() {
    const textService: TextService = ServiceProvider.resolveService(CoreServiceKey.TEXT);
    MessageFactoryProvider.registerMessageFactory(
      CoreModelKey.APPLICATION_ERROR, new ApplicationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreModelKey.VALIDATION_ERROR, new ValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreErrorKey.OBJECT_VALIDATION_ERROR, new ObjectValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreErrorKey.FIELD_VALIDATION_ERROR, new FieldValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreErrorKey.HTTP_ERROR, new HttpErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreValidationKey.EQUAL, new ComparatorValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreValidationKey.NOT_EQUAL, new ComparatorValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreValidationKey.GREATER_THAN, new ComparatorValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreValidationKey.GREATER_THAN_OR_EQUAL, new ComparatorValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreValidationKey.LESS_THAN, new ComparatorValidationErrorMessageFactory(textService)
    );
    MessageFactoryProvider.registerMessageFactory(
      CoreValidationKey.LESS_THAN_OR_EQUAL, new ComparatorValidationErrorMessageFactory(textService)
    );
  }

  // DYNAMIC IMPORT IS NOT WORKING WITH COMMONJS MODULE RESOLUTION
  static registerLocales() {
    LocaleProvider.registerPackageLocale('de', 'kit-core', coreDeLocale);
    LocaleProvider.registerPackageLocale('en', 'kit-core', coreEnLocale);
    // LocaleProvider.registerPackageLocale('de', 'kit-core', async () => {
    //   return import('./locale/de.locale').then((mod) => mod.coreDeLocale);
    // });
    // LocaleProvider.registerPackageLocale('en', 'kit-core', async () => {
    //   return import('./locale/en.locale').then((mod) => mod.coreEnLocale);
    // });
  }
}
